import betButtonDefaultBg from '@assets/button-betdefault-enabled.svg'
import betButtonForbiddenBg from '@assets/button-betdefault-red-enabled.svg'
import betButtonActiveBg from '@assets/button-betselected-enabled.svg'
import betButtonForbiddenActiveBg from '@assets/button-betselected-red-enabled.svg'
import ArrowButton from '@components/Buttons/ArrowButton'
import BetSliderButton from '@components/BetSlider/BetSliderButton'
import {
    MAX_OFFSET_OF_VISIBLE_BET_OPTIONS,
    MIDDLE_OF_VISIBLE_BET_OPTIONS,
    NUMBER_OF_VISIBLE_BET_OPTIONS,
} from '@constants/betSlider'
import {
    BetButtonActionType,
    BetButtonModeType,
    BetSliderCollectOrStopAllMessage,
    BetSliderScrollStartedMessage,
    BetSliderStartFreespinMessage,
    ChangeOpacityComponents,
    GameUIMessageType,
    JackpotPhase,
    SpinButtonState,
    ViewportType,
    BetSliderDisableScrollingMessage,
    BetSliderSetActionMessage,
    BetSliderSetButtonsInteractiveMessage,
    BetSliderPreSetStateMessage,
    BetSliderEmitButtonClickedMessage,
    BetSliderKeyboardDownMessage,
    BetSliderSetScrollingIndexMessage,
    GameUIMessageScope,
    BetSliderSetBetsMessage,
    BetSliderBetOrSelectMessage,
    BetSliderCurrentIndexChangedMessage,
    BetSliderScrollToIndexMessage,
    BetSliderSetVisibilityMessage,
    BetSliderScrollFinishedMessage,
    BetSliderSelectedIndexChangedMessage,
    BetSliderUpdateDisabledOptions,
    BetOptionItem,
} from 'game-ui-types'
import { useGSAP } from '@gsap/react'
import { useOpacityToggle } from '@hooks'
import {
    useBetSliderStore,
    useBuyBonusStore,
    useConfigurationStore,
    useFreespinsStore,
    useJackpotStore,
    usePlayerInfoStore,
    useSpinButtonSectionStore,
    useDoubleChanceStore,
    useFreeBuyBonusStore,
    useFreeSpinsBonusesStore,
    useLuckySpinsStore,
    useNavigationStore,
} from '@stores'
import gsap from 'gsap/all'
import Draggable from 'gsap/dist/Draggable'
import InertiaPlugin from 'gsap/dist/InertiaPlugin'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatMoney } from '@utils/money'
import { betSliderConfig } from './config'
import { getBetValue } from '@utils/betOption'
import { TranslationKey, useTranslation } from '@amusnet/translations/react'

gsap.registerPlugin(useGSAP, Draggable, InertiaPlugin)

const BetSlider: React.FC = () => {
    const { t } = useTranslation()
    // zustand state
    // configuration store
    const viewport = useConfigurationStore((s) => s.viewport)
    const currency = useConfigurationStore((s) => s.currency)
    const currencyHideCents = useConfigurationStore((s) => s.currencyHideCents)
    const centsVisibilityMoneyLimit = useConfigurationStore((s) => s.centsVisibilityMoneyLimit)
    const shouldShowCentsOnMoneyLimit = useConfigurationStore((s) => s.shouldShowCentsOnMoneyLimit)
    const betOptions = useConfigurationStore((s) => s.betOptions)
    const setConfiguration = useConfigurationStore((s) => s.setConfiguration)
    const shouldBalanceUpdateImmediately = useConfigurationStore((s) => s.shouldBalanceUpdateImmediately)

    // bet slider store
    const currentIndex = useBetSliderStore((s) => s.currentIndex)
    const activeIndex = useBetSliderStore((s) => s.activeIndex)
    const activeIndexOffset = useBetSliderStore((s) => s.activeIndexOffset)
    const slideToIndex = useBetSliderStore((s) => s.scrollToIndex)
    const updateSlideToIndex = useBetSliderStore((s) => s.updateScrollToIndex)
    const updateActiveIndex = useBetSliderStore((s) => s.updateActiveIndex)
    const updateCurrentIndex = useBetSliderStore((s) => s.updateCurrentIndex)
    const updateActiveIndexOffset = useBetSliderStore((s) => s.updateActiveIndexOffset)
    const isScrolling = useBetSliderStore((s) => s.isScrolling)
    const setIsScrolling = useBetSliderStore((s) => s.setIsScrolling)
    const hideCentsWhenMultipleOf = useBetSliderStore((s) => s.hideCentsWhenMultipleOf)
    const setHideCentsWhenMultipleOf = useBetSliderStore((s) => s.setHideCentsWhenMultipleOf)
    const forbiddenBetOptionIndexes = useBetSliderStore((s) => s.forbiddenBetOptionIndexes)
    const setForbiddenBetOptionIndexes = useBetSliderStore((s) => s.setForbiddenBetOptionIndexes)
    const setIsScrollingFromBetChooser = useBetSliderStore((s) => s.setIsScrollingFromBetChooser)
    const isScrollingFromBetChooser = useBetSliderStore((s) => s.isScrollingFromBetChooser)
    // spin button section store
    const isLongPressSpinButton = useSpinButtonSectionStore((s) => s.isLongPress)
    const spinButtonState = useSpinButtonSectionStore((s) => s.state)
    const isAutoplaySectionOpened = useSpinButtonSectionStore((s) => s.isAutoplaySectionOpened)
    const isBetSectionOpened = useSpinButtonSectionStore((s) => s.isBetSectionOpened)
    const isAutoplayMode = useSpinButtonSectionStore((s) => s.isAutoplayMode)
    const isGambleActive = useSpinButtonSectionStore((s) => s.isGambleActive)

    const isWinAmountAnimating = usePlayerInfoStore((s) => s.isWinAmountAnimating)
    const currentFeatureMultiplier = useBuyBonusStore((s) => s.currentFeatureMultiplier)
    const jackpotPhase = useJackpotStore((s) => s.jackpotPhase)
    const isShowingFreespinsSplash = useFreespinsStore((s) => s.isShowingFreespinsSplash)
    const isDoubleChanceActivated = useDoubleChanceStore((s) => s.isDoubleChanceActivated)
    const isMenuOpen = useNavigationStore((s) => s.isMenuOpen)
    const isFreeBuyBonusModalOpen = useFreeBuyBonusStore((s) => s.isModalOpened)
    const isFreeSpinsBonusModalOpen = useFreeSpinsBonusesStore((s) => s.isModalOpened)
    const freeSpinsShowRemainingSpinsBanner = useFreeSpinsBonusesStore((s) => s.showRemainingSpinsBanner)
    const isFreespinsActive = useFreespinsStore((state) => state.isFreespinsActive)
    const isLuckySpinsBonusModalOpen = useLuckySpinsStore((s) => s.isModalOpened)
    const luckySpinsShowRemainingSpinsBanner = useLuckySpinsStore((s) => s.showRemainingSpinsBanner)

    // component configuration
    const betSliderPosition = useConfigurationStore((s) => s.getComponentConfig('BetSlider').position)

    const arrowLeftPosition = useConfigurationStore((s) => s.getComponentConfig('BetSliderArrowLeft').position)

    const arrowRightPosition = useConfigurationStore((s) => s.getComponentConfig('BetSliderArrowRight').position)

    // local state
    const [betSliderWidth, setBetSliderWidth] = useState(betSliderConfig[viewport].betSliderWidth)
    const { itemWidth, gap, betSliderHeight } = betSliderConfig[viewport]
    const [sideExtraSpaceNoGap, setSideExtraSpaceNoGap] = useState(betSliderConfig[viewport].sideExtraSpaceNoGap)
    const [areBetButtonsInteractive, setAreBetButtonsInteractive] = useState(true)
    const [requestedBetButtonsAction, setRequestedBetButtonsAction] = useState<BetButtonActionType>(
        BetButtonActionType.BET
    )
    const [scrollSnapValues, setScrollSnapValues] = useState<{ [currentIndexKey: number]: number }>({ 0: 0 })
    const [betButtonsActions, setBetButtonsActions] = useState<{ [betIndexKey: number]: BetButtonActionType }>({})
    const [mode, setMode] = useState<BetButtonModeType>(BetButtonModeType.BET)
    const [displayBetSlider, setDisplayBetSlider] = useState('none')
    const [isIntroPlaying, setIsIntroPlaying] = useState(false)
    const [hasScroll, setHasScroll] = useState<boolean>(true)
    const [innerContainerPosX, setInnerContainerPosX] = useState(0)
    const [keyboardDownIndex, setKeyboardDownIndex] = useState(-1)
    const [disableOffsetAnimation, setDisableOffsetAnimation] = useState(false)
    const [isArrowDisabled, setIsArrowDisabled] = useState({ left: false, right: false })

    // refs
    const isWinAmountAnimatingRef = useRef<boolean>(isWinAmountAnimating)
    const activeIndexRef = useRef<number>(activeIndex)
    const currentIndexRef = useRef<number>(currentIndex)
    const betOptionsRef = useRef<number[] | BetOptionItem[]>(betOptions)
    const scrollSnapValuesRef = useRef<{ [currentIndexKey: number]: number }>(scrollSnapValues)
    const requestedBetButtonsActionRef = useRef<BetButtonActionType>(requestedBetButtonsAction)
    const betButtonsActionsRef = useRef<{ [betIndexKey: number]: BetButtonActionType }>(betButtonsActions)
    const isScrollingDisabledRef = useRef<boolean>(false)
    const [isScrollingDisabled, setIsScrollingDisabledState] = useState(false)
    const betSliderComponent = useRef<HTMLDivElement | null>(null)
    const outerContainerBetSlider = useRef<HTMLDivElement | null>(null)
    const innerContainerBetSlider = useRef<HTMLDivElement | null>(null)
    const draggableContainer = useRef<Draggable | null>(null)
    const scrollToTimeline = useRef<gsap.core.Timeline | null>(null)
    // TODO: Remove action update pendingActionUpdateRef and actionUpdateTimeoutRef
    // when migration from window.postMessage to uiBus is complete
    const pendingActionUpdateRef = useRef<{ action: BetButtonActionType; timestamp: number } | null>(null)
    const actionUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const { opacity, isComponentInteractive } = useOpacityToggle(ChangeOpacityComponents.BET_SLIDER, betSliderComponent)

    const componentOpacity = displayBetSlider === 'block' ? opacity : 0
    const [isInteractive, setIsInteractive] = useState(true)
    const isInteractiveRef = useRef(isInteractive)

    const shouldDisableBetSliderDraggingOnDesktop = useMemo(() => {
        const params = new URLSearchParams(window.location.search)

        return params.get('shouldDisableBetSliderDraggingOnDesktop') === 'true'
    }, [])

    const isDraggableEnabled = hasScroll && !isScrollingDisabled

    useEffect(() => {
        draggableContainer.current?.enabled(isDraggableEnabled)
    }, [isDraggableEnabled, viewport])

    const setIsScrollingDisabled = (isDisabled: boolean) => {
        setIsScrollingDisabledState(isDisabled)
        isScrollingDisabledRef.current = isDisabled
    }

    const handleCollectStopAllAction = (
        action: typeof BetButtonActionType.COLLECT | typeof BetButtonActionType.STOP_ALL,
        fromUser = true
    ) => {
        if (fromUser) {
            const collectOrStopAllMessage: BetSliderCollectOrStopAllMessage = {
                scope: GameUIMessageScope.BET_SLIDER,
                type: action,
            }

            window.postMessage(collectOrStopAllMessage)
        }
    }

    const onListMoveStart = (shouldAnimate: boolean, fromUser = true, suppressMessages = false) => {
        if (requestedBetButtonsActionRef.current === BetButtonActionType.COLLECT || isWinAmountAnimatingRef.current) {
            if (shouldBalanceUpdateImmediately) return // TODO: expo tournament and finnish requirement - prevent subtracting win amount from balance

            if (!suppressMessages) handleCollectStopAllAction(BetButtonActionType.COLLECT, fromUser)
        }

        if (!suppressMessages && shouldAnimate) {
            const scrollStartedMessage: BetSliderScrollStartedMessage = {
                scope: GameUIMessageScope.BET_SLIDER,
                type: GameUIMessageType.BET_SLIDER_SCROLL_STARTED,
            }

            window.postMessage(scrollStartedMessage)
        }

        setIsScrolling(true)
    }

    const onListMove = (event: Event) => {
        let containerXPos = Number((innerContainerBetSlider.current?._gsap.x as string).replace('px', ''))

        if (isNaN(containerXPos)) containerXPos = 0

        const outerContainerWidth = Number(outerContainerBetSlider.current?.style.width.replace('px', ''))
        const innerContainerWidth = Number(innerContainerBetSlider.current?.style.width.replace('px', ''))

        if (
            containerXPos > outerContainerWidth / 2 ||
            containerXPos < outerContainerWidth / 2 - innerContainerWidth ||
            !isInteractiveRef.current
        ) {
            draggableContainer.current?.endDrag(event)
        }

        setInnerContainerPosX(containerXPos)
    }

    const onListStopped = (suppressMessages = false) => {
        if (!useBetSliderStore.getState().isScrolling) return

        setIsScrolling(false)
        setIsIntroPlaying(false)
        updateSlideToIndex(-1)
        setIsScrollingFromBetChooser(false)

        if (!suppressMessages) {
            const scrollFinishedMessage: BetSliderScrollFinishedMessage = {
                scope: GameUIMessageScope.BET_SLIDER,
                type: GameUIMessageType.BET_SLIDER_SCROLL_FINISHED,
                activeIndex: activeIndexRef.current,
            }

            window.postMessage(scrollFinishedMessage)
        }
    }

    const playIntro = () => {
        if (scrollToTimeline.current && betOptionsRef.current.length > NUMBER_OF_VISIBLE_BET_OPTIONS) {
            const duration = 0.5

            setIsIntroPlaying(true)
            scrollToTimeline.current
                .to(innerContainerBetSlider.current, {
                    x: `-=${sideExtraSpaceNoGap * 2}`,
                    ease: 'none',
                    delay: 1,
                    duration,
                })
                .to(innerContainerBetSlider.current, {
                    x: `+=${sideExtraSpaceNoGap * 2}`,
                    ease: 'power2.in',
                    duration,
                    onComplete: () => onListStopped(),
                })

            setIsScrolling(true)
        }
    }

    const scrollTo = useCallback(
        (
            desiredIndex: number,
            animate = true,
            force = false,
            keepActiveIndexOffset?: number,
            fromUser = !force,
            suppressMessages = false
        ) => {
            if (isScrollingDisabledRef.current && !force) return

            let updatedActiveIndexOffset = activeIndexOffset

            if (keepActiveIndexOffset === undefined) {
                // In normal scenario keep chosen option in the middle
                updatedActiveIndexOffset = MIDDLE_OF_VISIBLE_BET_OPTIONS

                if (desiredIndex < 2 || !hasScroll) {
                    // If at the beginning do not show empty space on the left
                    updatedActiveIndexOffset = desiredIndex
                } else if (
                    betOptionsRef.current.length &&
                    desiredIndex > betOptionsRef.current.length - MAX_OFFSET_OF_VISIBLE_BET_OPTIONS
                ) {
                    // If at the end do not show empty space on the right
                    updatedActiveIndexOffset =
                        desiredIndex - (betOptionsRef.current.length - NUMBER_OF_VISIBLE_BET_OPTIONS)
                }
            }

            const targetIndex = Math.max(desiredIndex - updatedActiveIndexOffset, 0)

            // Disable arrows to prevent spam clicking if scrolling to the edges of the slider
            setIsArrowDisabled({
                left: targetIndex === 0,
                right: targetIndex === betOptions.length - NUMBER_OF_VISIBLE_BET_OPTIONS,
            })

            if (targetIndex === currentIndexRef.current && !force) {
                updateActiveIndexOffset(updatedActiveIndexOffset)
                setIsScrollingFromBetChooser(false)
                updateSlideToIndex(-1)

                return
            }

            scrollToTimeline.current?.clear()
            scrollToTimeline.current?.to(innerContainerBetSlider.current, {
                duration: 1,
                x: scrollSnapValuesRef.current[targetIndex],
                onStart: () => {
                    onListMoveStart(animate, fromUser, suppressMessages)
                    updateActiveIndexOffset(updatedActiveIndexOffset)
                },
                onUpdate: (e) => onListMove(e),
                onComplete: () => onListStopped(suppressMessages),
            })

            if (!animate) {
                scrollToTimeline.current?.progress(1)
            }
        },
        [activeIndex, betOptions, scrollSnapValues, requestedBetButtonsAction, isInteractive, hasScroll]
    )

    useEffect(() => {
        setIsInteractive(!isScrollingFromBetChooser)
    }, [isScrollingFromBetChooser])

    const setSelectedIndex = useCallback(
        (desiredIndex: number, shouldPositionList = false, recovery = false) => {
            // If item desired is not visible force it to be visible
            const isDesiredVisible =
                desiredIndex >= currentIndexRef.current &&
                desiredIndex < currentIndexRef.current + NUMBER_OF_VISIBLE_BET_OPTIONS

            const isDesiredVisibleAndActive = isDesiredVisible && desiredIndex === activeIndexRef.current

            if (isScrollingDisabledRef.current && isDesiredVisibleAndActive) return

            let updatedActiveIndexOffset = activeIndexRef.current
            let goingToScroll = false

            if (shouldPositionList) {
                const targetIndex = Math.max(
                    Math.min(
                        desiredIndex - MIDDLE_OF_VISIBLE_BET_OPTIONS,
                        betOptionsRef.current.length - NUMBER_OF_VISIBLE_BET_OPTIONS + 1
                    ),
                    0
                )

                if (desiredIndex - targetIndex !== updatedActiveIndexOffset) {
                    updatedActiveIndexOffset = desiredIndex - targetIndex
                    goingToScroll = true
                    scrollTo(targetIndex)
                }
            } else {
                if (desiredIndex - currentIndexRef.current !== updatedActiveIndexOffset) {
                    updatedActiveIndexOffset = desiredIndex - currentIndexRef.current

                    if (!isDesiredVisible || recovery) {
                        goingToScroll = true
                        // Although we specifically want to go to a certain index rather than brute forcing our way
                        // we go through the scrollTo method to have a one source of updating/checks
                        // The proper activeIndex is updated at the end of scrolling; if the user rotates during
                        // use effects on the viewport will reset the active index to the old one
                        // We want to make sure that from this point onwards this is the activeIndex until some other external entity changes it
                        updateActiveIndex(desiredIndex)
                        scrollTo(desiredIndex, false, true)
                    }
                }
            }

            // Let scrolling take care of the update if going to scroll
            if (!goingToScroll) {
                updateActiveIndexOffset(updatedActiveIndexOffset)
            }
        },
        [activeIndex, scrollSnapValues]
    )

    const isBetButtonFullyVisible = (betIndex: number) => {
        return betIndex >= currentIndexRef.current && betIndex < currentIndexRef.current + NUMBER_OF_VISIBLE_BET_OPTIONS
    }

    const onBetButtonClicked = useCallback(
        (betIndex: number, isEventFromKeyboard = false) => {
            // If gamble is active, don't allow to place a bet
            if (isGambleActive) {
                return
            }

            if (isScrolling) {
                // If button clicked while scrolling stop the scrolling only unless it is during intro, continue in that case
                onListStopped()
                scrollToTimeline.current?.kill()

                if (!isIntroPlaying) return
            }

            if (!isBetButtonFullyVisible(betIndex)) {
                // If bet is not one of the current visible buttons then it shouldn't be able to carry out the action
                return
            }

            const action =
                isEventFromKeyboard &&
                betButtonsActionsRef.current[betIndex] === BetButtonActionType.SELECT &&
                !forbiddenBetOptionIndexes.includes(betIndex)
                    ? BetButtonActionType.BET
                    : betButtonsActionsRef.current[betIndex]

            switch (action) {
                case BetButtonActionType.BET:
                case BetButtonActionType.SELECT: {
                    if (isScrollingDisabledRef.current && jackpotPhase !== JackpotPhase.PENDING) return

                    if (betIndex !== activeIndexRef.current && jackpotPhase !== JackpotPhase.PENDING) {
                        setSelectedIndex(betIndex, false)
                        updateSlideToIndex(-1)
                    }

                    const betOrSelectMessage: BetSliderBetOrSelectMessage = {
                        scope: GameUIMessageScope.BET_SLIDER,
                        type: action,
                        payload: { betIndex },
                    }

                    window.postMessage(betOrSelectMessage)
                    break
                }
                case BetButtonActionType.STOP_ALL:
                case BetButtonActionType.COLLECT:
                    handleCollectStopAllAction(action)
                    break
                case BetButtonActionType.START_FREESPIN: {
                    const startFreespinMessage: BetSliderStartFreespinMessage = {
                        scope: GameUIMessageScope.BET_SLIDER,
                        type: GameUIMessageType.BET_SLIDER_START_FREESPIN,
                    }

                    setAreBetButtonsInteractive(false)
                    window.postMessage(startFreespinMessage)
                    break
                }
                default:
                    console.error(`[Bet Slider] Invalid action :: ${action} in betButton with id :: ${betIndex}`)
                    break
            }
            setKeyboardDownIndex(-1)
        },
        [
            activeIndex,
            betButtonsActions,
            isScrolling,
            forbiddenBetOptionIndexes,
            isIntroPlaying,
            jackpotPhase,
            isGambleActive,
        ]
    )

    const onArrowClick = (direction: string) => {
        if (!hasScroll || isScrollingDisabledRef.current) return

        if (isScrolling) {
            // If button clicked while scrolling stop the scrolling
            onListStopped()
            scrollToTimeline.current?.kill()
        }

        if (direction === 'right') {
            scrollTo(
                Math.min(
                    currentIndexRef.current + (NUMBER_OF_VISIBLE_BET_OPTIONS + activeIndexOffset),
                    betOptionsRef.current.length - NUMBER_OF_VISIBLE_BET_OPTIONS + activeIndexOffset
                ),
                true,
                true,
                activeIndexOffset,
                true
            )
        } else {
            scrollTo(
                Math.max(
                    currentIndexRef.current - (NUMBER_OF_VISIBLE_BET_OPTIONS - activeIndexOffset),
                    activeIndexOffset
                ),
                true,
                true,
                activeIndexOffset,
                true
            )
        }
    }

    const listMask =
        viewport === ViewportType.MOBILE_PORTRAIT
            ? ' linear-gradient(90deg, rgba(217, 217, 217, 0.30) 0%, #D9D9D9 3%, #D9D9D9 97%, rgba(217, 217, 217, 0.30) 100%)'
            : 'linear-gradient(90deg, rgba(217, 217, 217, 0.00) 0%, #D9D9D9 4.5%, #D9D9D9 95%, rgba(217, 217, 217, 0.00) 100%)'

    useGSAP(
        () => {
            const isDraggingDisabled = viewport === ViewportType.DESKTOP && shouldDisableBetSliderDraggingOnDesktop

            if (betOptionsRef.current.length && hasScroll && !isDraggingDisabled) {
                if (draggableContainer.current) draggableContainer.current.kill()

                const snap = Object.values(scrollSnapValuesRef.current)

                draggableContainer.current = Draggable.create(innerContainerBetSlider.current, {
                    type: 'x',
                    edgeResistance: 0.95,
                    minimumMovement: 5,
                    inertia: true,
                    bounds: { minX: snap[0], maxX: snap[snap.length - 1] },
                    throwProps: true,
                    zIndexBoost: false,
                    allowContextMenu: true,
                    overshootTolerance: 0,
                    snap,
                    onPress: (e) => {
                        // If not left button cancel dragging
                        if (e.button !== 0) {
                            draggableContainer.current?.endDrag(e)
                            e.stopPropogation()
                        }
                    },
                    onDragStart(e: MouseEvent | TouchEvent) {
                        if (!isInteractiveRef.current || isScrollingDisabledRef.current) {
                            this.endDrag(e)

                            return
                        }

                        scrollToTimeline.current?.clear()
                        onListMoveStart(true)
                        // On drag reset arrows and default to currentIndex check in HTML render
                        setIsArrowDisabled({
                            left: false,
                            right: false,
                        })
                    },
                    onDrag: (e) => onListMove(e),
                    onThrowUpdate: (e) => onListMove(e),
                    onDragEnd: () => {
                        // onThrowComplete does not run when inertia is killed (e.g. draggableContainer.current?.endDrag(e))
                        // finalize here unless a throw is still active.
                        if (!draggableContainer.current?.isThrowing) {
                            onListStopped()
                        }
                    },
                    onThrowComplete: () => onListStopped(),
                })[0]

                const shouldForceDisable = shouldDisableBetSliderDraggingOnDesktop

                draggableContainer.current.enabled(
                    shouldForceDisable ? false : hasScroll && !isScrollingDisabledRef.current
                )
            }

            if (!scrollToTimeline.current) {
                scrollToTimeline.current = gsap.timeline()
            }
        },
        {
            scope: outerContainerBetSlider,
            dependencies: [scrollSnapValues],
        }
    )

    const getSnapValues = () => {
        const values: { [currentIndexKey: number]: number } = { 0: 0 }

        for (let i = 1; i <= betOptionsRef.current.length - NUMBER_OF_VISIBLE_BET_OPTIONS; i++) {
            const position = (itemWidth + gap) * i

            values[i] = -position
        }

        return values
    }

    useEffect(() => {
        const interactive =
            isComponentInteractive &&
            areBetButtonsInteractive &&
            !isLongPressSpinButton &&
            Boolean(componentOpacity) &&
            !isShowingFreespinsSplash &&
            !isIntroPlaying &&
            !isFreeBuyBonusModalOpen &&
            !isLuckySpinsBonusModalOpen &&
            !luckySpinsShowRemainingSpinsBanner &&
            !isFreeSpinsBonusModalOpen &&
            !freeSpinsShowRemainingSpinsBanner

        setIsInteractive(interactive)
        isInteractiveRef.current = interactive
    }, [
        isComponentInteractive,
        areBetButtonsInteractive,
        isLongPressSpinButton,
        componentOpacity,
        isShowingFreespinsSplash,
        isIntroPlaying,
        isShowingFreespinsSplash,
        isFreeBuyBonusModalOpen,
        isLuckySpinsBonusModalOpen,
        luckySpinsShowRemainingSpinsBanner,
        isFreeSpinsBonusModalOpen,
        freeSpinsShowRemainingSpinsBanner,
    ])

    useEffect(() => {
        betOptionsRef.current = betOptions

        const hasScrollingFeature = betOptions.length > NUMBER_OF_VISIBLE_BET_OPTIONS

        setHasScroll(hasScrollingFeature)

        if (hasScrollingFeature) {
            setSideExtraSpaceNoGap(betSliderConfig[viewport].sideExtraSpaceNoGap)
            setBetSliderWidth(betSliderConfig[viewport].betSliderWidth)
        } else {
            setSideExtraSpaceNoGap(0)
            setBetSliderWidth(gap + (itemWidth + gap) * betOptions.length)
        }

        const newSnapValues = getSnapValues()

        const valuesChanged = Object.entries(newSnapValues).find(
            ([index, x]) => scrollSnapValuesRef.current[+index] !== x
        )

        if (!valuesChanged) return

        if (isScrolling) {
            onListStopped()
            scrollToTimeline.current?.kill()
        }

        scrollSnapValuesRef.current = newSnapValues
        setScrollSnapValues(scrollSnapValuesRef.current)

        // Ensure scrolling to the correct index on viewport change by using slideToIndex
        const targetIndex = slideToIndex !== -1 ? slideToIndex : activeIndexRef.current

        scrollTo(targetIndex, false, true, undefined, undefined, true)
    }, [betOptions, viewport, slideToIndex])

    useEffect(() => {
        requestedBetButtonsActionRef.current = requestedBetButtonsAction
    }, [requestedBetButtonsAction])

    useEffect(() => {
        setIsScrollingDisabled(
            isAutoplayMode ||
                isLongPressSpinButton ||
                spinButtonState === SpinButtonState.SEND_TURBO_STATE ||
                spinButtonState === SpinButtonState.SEND_STOP_STATE ||
                jackpotPhase === JackpotPhase.PENDING ||
                isGambleActive ||
                isFreespinsActive
        )
    }, [spinButtonState, isLongPressSpinButton, jackpotPhase, isAutoplayMode, isGambleActive, isFreespinsActive])

    useEffect(() => {
        isWinAmountAnimatingRef.current = isWinAmountAnimating
    }, [isWinAmountAnimating])

    useEffect(() => {
        activeIndexRef.current = activeIndex
    }, [activeIndex])

    useEffect(() => {
        betButtonsActionsRef.current = betButtonsActions
    }, [betButtonsActions])

    useEffect(() => {
        currentIndexRef.current = currentIndex

        const newActiveIndex =
            betOptionsRef.current.length - 1 >= 0
                ? Math.min(currentIndexRef.current + activeIndexOffset, betOptions.length - 1)
                : currentIndexRef.current + activeIndexOffset

        if (newActiveIndex !== activeIndexRef.current) {
            updateActiveIndex(newActiveIndex)

            const selectedIndexChangedMessage: BetSliderSelectedIndexChangedMessage = {
                scope: GameUIMessageScope.BET_SLIDER,
                type: GameUIMessageType.BET_SLIDER_SELECTED_INDEX_CHANGED,
                index: newActiveIndex,
                isScrolling,
            }

            window.postMessage(selectedIndexChangedMessage)
        }
    }, [currentIndex, activeIndexOffset, betOptions])

    useEffect(() => {
        const [newIndexKey] = Object.entries(scrollSnapValuesRef.current)
            .reverse()
            .find(([, position]) => {
                return innerContainerPosX < position + (itemWidth + gap) / 2
            }) ?? [undefined]

        const newIndex = newIndexKey ? Number(newIndexKey) : currentIndexRef.current

        if (newIndex === currentIndexRef.current) return

        updateCurrentIndex(newIndex)

        const currentIndexChangedMessage: BetSliderCurrentIndexChangedMessage = {
            scope: GameUIMessageScope.BET_SLIDER,
            type: GameUIMessageType.BET_SLIDER_CURRENT_INDEX_CHANGED,
            payload: { index: newIndex },
        }

        window.postMessage(currentIndexChangedMessage)
    }, [innerContainerPosX, scrollSnapValues])

    useEffect(() => {
        if (slideToIndex === -1) return

        scrollTo(slideToIndex, !isMenuOpen)
    }, [slideToIndex])

    useEffect(() => {
        const actionsMap = {} as { [betIndexKey: number]: BetButtonActionType }

        for (let betIndex = 0; betIndex < betOptionsRef.current.length; betIndex++) {
            // if mode === BetButtonModeType.EXPLICIT_BET
            // --- non active are SELECT and active is BET when request is BET action otherwise use whatever requested action is
            // if mode === BetButtonModeType.ONLY_SELECT
            // --- all of them are SELECT action and user can only select a bet
            // if mode === BetButtonModeType.BET
            // --- use requested action for all
            const isActive = betIndex === activeIndexRef.current
            const shouldNormalButtonBeSelect = !isActive && mode === BetButtonModeType.EXPLICIT_BET
            const shouldButtonBeSelect =
                requestedBetButtonsAction === BetButtonActionType.BET &&
                (shouldNormalButtonBeSelect ||
                    forbiddenBetOptionIndexes.includes(betIndex) ||
                    mode === BetButtonModeType.ONLY_SELECT)

            const realAction = shouldButtonBeSelect ? BetButtonActionType.SELECT : requestedBetButtonsAction

            actionsMap[betIndex] = realAction
        }

        setBetButtonsActions(actionsMap)
    }, [requestedBetButtonsAction, betOptions, activeIndex, mode, forbiddenBetOptionIndexes])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const type = rawData.type
            const scope = rawData.scope

            if (
                scope !== GameUIMessageScope.MAIN_BUTTONS &&
                scope !== GameUIMessageScope.MAIN_BUTTONS_STATE_MANAGER &&
                scope !== GameUIMessageScope.BUY_BONUS
            ) {
                return
            }

            switch (type) {
                case GameUIMessageType.BUY_BONUS_CONFIRM_BUTTON_CLICKED:
                    setAreBetButtonsInteractive(false)
                    // Setting progress to 1 is needed in order to avoid situations while the bet slider scroll is going on
                    // to select different bet value than the one we bought the buy bonus with due to enabled interactivity
                    // from another message received.
                    scrollToTimeline.current?.progress(1)

                    break
                case GameUIMessageType.BET_SLIDER_SET_VISIBILITY: {
                    const data = rawData as BetSliderSetVisibilityMessage

                    setDisplayBetSlider(data.value ? 'block' : 'none')

                    break
                }
                case GameUIMessageType.BET_SLIDER_SET_BETS: {
                    const data = rawData as BetSliderSetBetsMessage

                    setDisableOffsetAnimation(data.disableOffsetAnimation ?? false)
                    setMode(data.betButtonsMode)
                    setForbiddenBetOptionIndexes(data.isForbidden ? [...Array(betOptions.length).keys()] : [])
                    // setMaxBet(data.maxBet)

                    // TODO: Readded until all engines are up to date - remove when ready
                    if (data.totalBets) setConfiguration({ betOptions: data.totalBets })

                    setDisplayBetSlider('block')
                    setHideCentsWhenMultipleOf(data.hideCentsWhenMultipleOf)
                    break
                }
                case GameUIMessageType.BET_SLIDER_SCROLL_TO_INDEX: {
                    const data = rawData as BetSliderScrollToIndexMessage

                    scrollTo(data.betIndex)
                    break
                }
                case GameUIMessageType.BET_SLIDER_PLAY_INTRO_ANIM:
                    if (displayBetSlider === 'none') return

                    playIntro()
                    break
                case GameUIMessageType.BET_SLIDER_DISABLE_SCROLLING: {
                    const data = rawData as BetSliderDisableScrollingMessage

                    setIsScrollingDisabled(Boolean(data.disabled))
                    break
                }
                case GameUIMessageType.BET_SLIDER_SET_ACTION: {
                    const data = rawData as BetSliderSetActionMessage

                    // Clear any pending action update from BET_SLIDER_PRE_SET_STATE
                    if (actionUpdateTimeoutRef.current) {
                        clearTimeout(actionUpdateTimeoutRef.current)
                        actionUpdateTimeoutRef.current = null
                    }

                    pendingActionUpdateRef.current = null

                    // Apply the final action immediately
                    setRequestedBetButtonsAction(data.betButtonsAction)
                    break
                }
                case GameUIMessageType.BET_SLIDER_SET_BUTTONS_INTERACTIVE: {
                    const data = rawData as BetSliderSetButtonsInteractiveMessage

                    setAreBetButtonsInteractive(data.betButtonsInteractive)
                    break
                }
                case GameUIMessageType.BET_SLIDER_PRE_SET_STATE: {
                    const data = rawData as BetSliderPreSetStateMessage

                    // Store the action as pending instead of applying it immediately
                    pendingActionUpdateRef.current = {
                        action: data.betButtonsAction,
                        timestamp: Date.now(),
                    }

                    // Update interactivity immediately as it's not part of the race condition
                    setAreBetButtonsInteractive(data.betButtonsInteractive)

                    // Clear any existing timeout
                    if (actionUpdateTimeoutRef.current) {
                        clearTimeout(actionUpdateTimeoutRef.current)
                    }

                    // Set a timeout to apply the action if BET_SLIDER_SET_ACTION doesn't arrive
                    // This ensures the action is eventually applied even if BET_SLIDER_SET_ACTION is delayed/missing
                    actionUpdateTimeoutRef.current = setTimeout(() => {
                        if (pendingActionUpdateRef.current) {
                            setRequestedBetButtonsAction(pendingActionUpdateRef.current.action)
                            pendingActionUpdateRef.current = null
                        }

                        actionUpdateTimeoutRef.current = null
                    }, 100)

                    break
                }
                case GameUIMessageType.BET_SLIDER_EMIT_BUTTON_CLICKED: {
                    const data = rawData as BetSliderEmitButtonClickedMessage

                    if (data.isActualIndex) {
                        onBetButtonClicked(data.betIndex, Boolean(data.fromKeyboard))
                    } else {
                        onBetButtonClicked(currentIndexRef.current + data.betIndex, Boolean(data.fromKeyboard))
                    }

                    break
                }
                case GameUIMessageType.BET_SLIDER_KEYBOARD_DOWN: {
                    const data = rawData as BetSliderKeyboardDownMessage

                    setKeyboardDownIndex(currentIndexRef.current + data.betIndex)
                    break
                }
                case GameUIMessageType.BET_SLIDER_KEYBOARD_UP:
                    setKeyboardDownIndex(-1)
                    break
                case GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX: {
                    const data = rawData as BetSliderSetScrollingIndexMessage

                    setSelectedIndex(data.currentBetIndex, data.shouldPositionList, data.recovery)
                    break
                }
                case GameUIMessageType.BET_SLIDER_UPDATE_DISABLED_OPTIONS: {
                    const data = rawData as BetSliderUpdateDisabledOptions

                    setForbiddenBetOptionIndexes(data.forbiddenIndexes)
                    break
                }
                default:
                    break
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)

            // Cleanup pending action timeout on unmount
            if (actionUpdateTimeoutRef.current) {
                clearTimeout(actionUpdateTimeoutRef.current)
            }
        }
    }, [])

    const getBetButtonActionLabel = useCallback(
        (betIndex: number) => {
            const action = betButtonsActions[betIndex]

            if (!action) return ''

            //in order to avoid adding START_FREESPIN key to localization
            if (action === BetButtonActionType.START_FREESPIN) {
                return t('gameui.bet')
            }

            if (action === BetButtonActionType.NONE || action === BetButtonActionType.AUTO) return ''

            const actionLabel =
                forbiddenBetOptionIndexes.includes(betIndex) &&
                (action === BetButtonActionType.BET || action === BetButtonActionType.SELECT)
                    ? ''
                    : t(`gameui.${action}` as TranslationKey)

            return actionLabel
        },
        [betButtonsActions, forbiddenBetOptionIndexes]
    )

    const renderButtons = useMemo(() => {
        return betOptions.map((betOption, index) => {
            const isActive = activeIndex === index
            const value = getBetValue(betOption) * currentFeatureMultiplier

            let backgroundImage = isActive ? betButtonActiveBg : betButtonDefaultBg

            if (forbiddenBetOptionIndexes.includes(index)) {
                backgroundImage = isActive ? betButtonForbiddenActiveBg : betButtonForbiddenBg
            }

            const className = `bet-slider-button-${index} absolute flex flex-col items-center justify-start text-white focus:outline-none`

            const shouldHideCents =
                currencyHideCents ||
                (value > centsVisibilityMoneyLimit &&
                    !shouldShowCentsOnMoneyLimit &&
                    Boolean(hideCentsWhenMultipleOf && value % hideCentsWhenMultipleOf === 0))

            return (
                <BetSliderButton
                    key={index}
                    index={index}
                    componentOpacity={componentOpacity}
                    keyboardDownIndex={keyboardDownIndex}
                    posX={sideExtraSpaceNoGap + gap + (itemWidth + gap) * index}
                    money={formatMoney(value, shouldHideCents)}
                    onClick={() => {
                        if (isInteractiveRef.current) onBetButtonClicked(index)
                    }}
                    className={className}
                    backgroundImage={backgroundImage}
                    actionLabel={getBetButtonActionLabel(index)}
                    currency={currency}
                    isActive={isActive}
                    isInteractive={isInteractive}
                    isScrolling={isScrolling}
                    isButtonFullyVisible={isBetButtonFullyVisible(index)}
                    disableOffsetAnimation={disableOffsetAnimation}
                />
            )
        })
    }, [
        currency,
        betOptions,
        forbiddenBetOptionIndexes,
        opacity,
        displayBetSlider,
        keyboardDownIndex,
        displayBetSlider,
        isInteractive,
        betButtonsActions,
        isScrolling,
        currentFeatureMultiplier,
        viewport,
        isDoubleChanceActivated,
        isGambleActive,
    ])

    const sliderDimensions = {
        width: `${betSliderWidth}px`,
        height: `${betSliderHeight}px`,
    }

    return (
        <div
            id="bet-slider"
            ref={betSliderComponent}
            className="absolute left-1/2 transform: -translate-x-1/2 overflow-visible"
            style={{
                ...betSliderPosition,
                ...sliderDimensions,
                display: displayBetSlider,
                zIndex: isAutoplaySectionOpened || isBetSectionOpened || isMenuOpen ? 10 : 11,
            }}
        >
            <div
                id="outer-bet-slider"
                ref={outerContainerBetSlider}
                className="absolute left-1/2 transform: -translate-x-1/2 overflow-x-clip"
                style={{
                    ...sliderDimensions,
                    mask: hasScroll ? listMask : undefined,
                }}
            >
                <div
                    id="inner-bet-slider"
                    ref={innerContainerBetSlider}
                    style={{
                        width: `${sideExtraSpaceNoGap * 2 + betOptions.length * itemWidth + (betOptions.length + 1) * gap}px`,
                    }}
                >
                    {renderButtons}
                </div>
            </div>
            {viewport === ViewportType.DESKTOP && hasScroll && (
                <ArrowButton
                    id="bet-slider-left-arrow"
                    className="absolute"
                    style={{ ...arrowLeftPosition }}
                    direction="left"
                    disabled={!isInteractive || isScrollingDisabled || isArrowDisabled.left || currentIndex === 0}
                    onClick={() => onArrowClick('left')}
                />
            )}
            {viewport === ViewportType.DESKTOP && hasScroll && (
                <ArrowButton
                    id="bet-slider-right-arrow"
                    className="absolute"
                    style={{ ...arrowRightPosition }}
                    direction="right"
                    disabled={
                        !isInteractive ||
                        isScrollingDisabled ||
                        isArrowDisabled.right ||
                        currentIndex === betOptions.length - NUMBER_OF_VISIBLE_BET_OPTIONS
                    }
                    onClick={() => onArrowClick('right')}
                />
            )}
        </div>
    )
}

export default BetSlider
